import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
const supabaseBaseUrl = new URL(supabaseUrl);

export const SUPABASE_AUTH_STORAGE_KEY = `sb-${
  supabaseBaseUrl.hostname.split(".")[0]
}-auth-token`;
export const SUPABASE_IMPERSONATION_STORAGE_KEY = `${SUPABASE_AUTH_STORAGE_KEY}-impersonation`;
export const IMPERSONATION_MODE_KEY = "admin_impersonation_mode";

export const IS_IMPERSONATION_SESSION = (() => {
  if (typeof window === "undefined") return false;

  try {
    const isImpersonationEntry =
      window.location.pathname === "/admin/impersonate" &&
      new URLSearchParams(window.location.search).has("token_hash");

    if (isImpersonationEntry) {
      window.sessionStorage.setItem(IMPERSONATION_MODE_KEY, "true");
      return true;
    }

    return window.sessionStorage.getItem(IMPERSONATION_MODE_KEY) === "true";
  } catch {
    return false;
  }
})();

export const clearActiveSupabaseAuthStorage = () => {
  if (typeof window === "undefined") return;

  try {
    const storage = IS_IMPERSONATION_SESSION
      ? window.sessionStorage
      : window.localStorage;
    const storageKey = IS_IMPERSONATION_SESSION
      ? SUPABASE_IMPERSONATION_STORAGE_KEY
      : SUPABASE_AUTH_STORAGE_KEY;
    storage.removeItem(storageKey);
  } catch {}
};

export const clearImpersonationStorage = () => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(IMPERSONATION_MODE_KEY);
    window.sessionStorage.removeItem(SUPABASE_IMPERSONATION_STORAGE_KEY);
  } catch {}
};

// Do NOT set a global Content-Type header.
// Supabase JS sets correct headers per request (especially for storage uploads),
// and a global Content-Type: application/json would incorrectly tag binary uploads.
//
// Cross-subdomain auth (e.g. procertcompliance.com → praxjobs.procertcompliance.com)
// is handled via URL hash token passing in useAuth.jsx and login/index.jsx.
// Normal sessions use localStorage because Supabase sessions exceed the 4KB
// cookie limit. Impersonation sessions use a tab-scoped sessionStorage key so
// they cannot replace the administrator's session in another tab.
const _supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    storageKey: IS_IMPERSONATION_SESSION
      ? SUPABASE_IMPERSONATION_STORAGE_KEY
      : SUPABASE_AUTH_STORAGE_KEY,
    ...(IS_IMPERSONATION_SESSION ? { storage: window.sessionStorage } : {}),
  },
});

// Patch functions.invoke to always send a fresh access token.
// supabase-js _getAccessToken() falls back to the anon key when getSession()
// returns null (race condition, expired session, cross-subdomain SSO).
// The anon key is not a valid JWT, so the edge function relay rejects with
// {"code":401,"message":"Invalid JWT"}. This wrapper ensures a valid access
// token is always passed explicitly via the Authorization header.
const _origFunctionsGetter = Object.getOwnPropertyDescriptor(
  Object.getPrototypeOf(_supabase),
  "functions",
);

Object.defineProperty(_supabase, "functions", {
  get() {
    const client = _origFunctionsGetter.get.call(this);
    const origInvoke = client.invoke.bind(client);

    client.invoke = async (functionName, options = {}) => {
      // Only inject token if caller didn't already set Authorization
      if (!options.headers?.Authorization) {
        try {
          const { data } = await _supabase.auth.getSession();
          if (data.session?.access_token) {
            options = {
              ...options,
              headers: {
                ...options.headers,
                Authorization: `Bearer ${data.session.access_token}`,
              },
            };
          }
        } catch {
          // Fall through — let the original invoke handle it
        }
      }
      return origInvoke(functionName, options);
    };

    return client;
  },
});

export const supabase = _supabase;
